import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PriceCorrectionInput {
  scan_id: string;
  corrected_price: number;
  notes?: string;
  decision_id?: string;
}

// `submit_price_correction` was added in migration 20260516010000 but the
// generated Supabase types in `src/integrations/supabase/types.ts` haven't
// been regenerated yet (deferred to Phase 1). Cast the function name to
// `never` so the union-of-known-rpc-names check passes; the call itself
// stays a method invocation on `supabase` so `this` binding is preserved
// (the rebound-method bug surfaced as "Cannot read properties of undefined
// (reading 'rest')").
export function usePriceCorrection() {
  const mutation = useMutation<string, Error, PriceCorrectionInput>({
    mutationFn: async (input) => {
      const args = {
        p_scan_id: input.scan_id,
        p_corrected_price: input.corrected_price,
        p_correction_source: "user_estimate",
        p_notes: input.notes,
        p_decision_id: input.decision_id,
      } as never;
      const { data, error } = await supabase.rpc(
        "submit_price_correction" as never,
        args,
      );
      if (error) throw new Error(error.message);
      return data as unknown as string;
    },
  });

  return {
    submit: mutation.mutate,
    submitAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    correctionId: mutation.data,
    reset: mutation.reset,
  };
}
